import {
  isOrganizationContextMismatch,
  ORGANIZATION_CONTEXT_HEADER,
  queryKeys
} from '@/api'
import { idmQueryKeys } from '@/api/momo/idm/idm-query-keys'
import { getCurrentUser } from '@/api/momo/idm/users'
import { getOrganization } from '@/api/momo/users/organizations'
import { sessionAtom } from '@/atoms/app-atoms'
import { SuspenseFallback } from '@/components/suspense-fallback'
import { toastError } from '@/components/toast/toast'
import { signOutOfCognito } from '@/app/_auth/sign-out'
import { recordHandledSignOut } from '@/helpers/handled-signout-telemetry'
import * as Sentry from '@sentry/nextjs'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import axios from 'axios'
import { useSetAtom } from 'jotai'
import { Session } from 'next-auth'
import { getSession } from 'next-auth/react'
import React, { useEffect, useRef } from 'react'

// Long enough to read. The app retries a 429 by itself, so the toast need not
// stay after the data loads.
const RATE_LIMIT_TOAST_MS = 6000

export interface AuthContextProviderProps {
  children: React.ReactNode
}

export function AuthContextProvider({ children }: AuthContextProviderProps) {
  const setAuthContext = useSetAtom(sessionAtom)
  const queryClient = useQueryClient()

  // Several in-flight requests can be rejected together when the organization
  // context goes stale; converge (toast + refetch) only once for the burst.
  const lastMismatchHandledAt = useRef(0)

  // Register the global 401 signOut interceptor once on mount.
  useEffect(() => {
    // Requests rejected together share one session renewal.
    let pendingRenewal: Promise<Session | null> | null = null
    const renewSession = () => {
      pendingRenewal ??= getSession()
        .then((session) => {
          if (session) {
            axios.defaults.headers.common['Authorization'] =
              `Bearer ${session.accessToken}`
            // Publish the new expiry too, so the layout's expiry check sees it.
            queryClient.setQueryData(queryKeys.users.session(), session)
          }
          return session
        })
        .finally(() => {
          pendingRenewal = null
        })
      return pendingRenewal
    }

    const interceptorId = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        if (error.response?.status === 401) {
          // A tab that was hidden or asleep can send a request before the
          // session refetch below renews the Cognito token. Renew it once and
          // retry before treating the 401 as a real sign-out.
          const sentAuthorization = error.config?.headers?.Authorization
          if (
            typeof sentAuthorization === 'string' &&
            sentAuthorization.startsWith('Bearer ')
          ) {
            const session = await renewSession()
            const renewedAuthorization = session?.accessToken
              ? `Bearer ${session.accessToken}`
              : undefined
            // An unchanged token means renewal failed (or the API rejected a
            // fresh token), so retrying would only 401 again.
            if (
              renewedAuthorization &&
              renewedAuthorization !== sentAuthorization
            ) {
              error.config.headers.Authorization = renewedAuthorization
              return axios.request(error.config)
            }
          }

          await signOutOfCognito()
          recordHandledSignOut(error)
        }

        // The API rejected a request because the viewed organization was
        // switched in another tab after this one loaded. Correct the header
        // right away so follow-up requests succeed, then refetch everything
        // so this tab converges on the new organization.
        if (isOrganizationContextMismatch(error)) {
          // Always correct the header — even mid-cooldown the context may
          // have changed again, and skipping this would keep requests failing.
          const effectiveOrganizationID =
            error.response.data?.effectiveOrganizationID
          if (typeof effectiveOrganizationID === 'string') {
            axios.defaults.headers.common[ORGANIZATION_CONTEXT_HEADER] =
              effectiveOrganizationID
          }

          // Only the toast + refetch are debounced for the burst.
          if (Date.now() - lastMismatchHandledAt.current > 2000) {
            lastMismatchHandledAt.current = Date.now()
            toastError(
              'Your viewed organization was changed in another tab, so this action was not saved.'
            )
            queryClient.invalidateQueries()
          }
        }

        // The API limits how fast one user can call it (DEV-1754). The id
        // keeps a burst of limited requests to one toast.
        if (error.response?.status === 429) {
          toastError(
            'Too many requests. Wait a moment, then try again.',
            RATE_LIMIT_TOAST_MS,
            'too-many-requests'
          )
        }

        return Promise.reject(error)
      }
    )

    return () => {
      axios.interceptors.response.eject(interceptorId)
    }
  }, [queryClient])

  // Hold rendering of children until this query resolves. The queryFn installs
  // the global axios Authorization header before returning, so gating on
  // isPending guarantees no child component can fire an axios call before the
  // header is set — which would otherwise 401 and trip the signOut interceptor.
  const { data: session, isPending: isSessionPending } = useQuery({
    queryKey: queryKeys.users.session(),
    queryFn: async () => {
      const session = await getSession()

      // The server could not renew an expired Cognito token. Sign out now,
      // before a request fails and loses what the user was doing.
      if (session?.error === 'TokenExpired') {
        await signOutOfCognito()
        return null
      }

      // Setting axios global auth header
      // if we got a session.
      if (session) {
        // TODO: RT - (deferred) should be able to complete remove this once all api calls go to BEFE
        axios.defaults.headers.common['Authorization'] =
          `Bearer ${session.accessToken}`

        localStorage.setItem('hasLoggedIn', 'true')
      }

      return session ?? null
    },
    staleTime: 5 * 60 * 1000, // 5 minutes.
    gcTime: 5 * 60 * 1000, // 5 minutes.
    // Refresh every 25 minutes so the Cognito access token (30-min TTL) is
    // always renewed before it expires, regardless of user navigation activity.
    // Keep going while the tab is hidden, or it comes back with an expired token.
    refetchInterval: 25 * 60 * 1000,
    refetchIntervalInBackground: true
  })

  const { data: account } = useQuery({
    queryKey: idmQueryKeys.users.current(),
    queryFn: async () => {
      return await getCurrentUser()
    },
    enabled:
      !!session && axios.defaults.headers.common.Authorization != undefined,
    staleTime: 5 * 60 * 1000, // 5 minutes.
    gcTime: 5 * 60 * 1000 // 5 minutes.
  })

  // Keyed by the account's effective organization: when a Super Admin switches
  // the organization they are viewing as, the new key starts a fresh cache
  // entry instead of serving the previous organization's data. While it loads,
  // `organization` is undefined, so the atom below keeps publishing the old
  // consistent account/organization pair rather than a mixed one.
  const { data: organization, isError: isOrganizationError } = useQuery({
    queryKey: queryKeys.users.organization(account?.OrganizationID),
    queryFn: async () => {
      if (account) {
        return await getOrganization(account.OrganizationID)
      }
    },
    enabled: !!session && !!account,
    staleTime: 5 * 60 * 1000, // 5 minutes.
    gcTime: 5 * 60 * 1000 // 5 minutes.
  })

  // Stamp every request with the organization this tab is acting as. The API
  // compares it against the server-side selection and rejects writes when the
  // organization was switched from another tab in the meantime.
  useEffect(() => {
    if (account) {
      axios.defaults.headers.common[ORGANIZATION_CONTEXT_HEADER] =
        account.OrganizationID
    }
  }, [account])

  // Publish the account as soon as it lands, but only alongside an organization
  // that is actually its own. The organization query is keyed on the account's
  // OrganizationID, so after a switch it reloads and the previous organization is
  // still in hand — publishing that pair would be consistent but stale, and stale
  // is what components send to the server. The axios organization-context header
  // has already moved on by then, so the API's guard sees nothing wrong and the
  // request is simply rejected for an organization the token no longer belongs to
  // (API-1E). Leaving `organization` undefined instead puts consumers in their
  // loading state for the moment it takes to arrive.
  useEffect(() => {
    if (!session || !account) {
      return
    }

    setAuthContext({
      session,
      account,
      organization:
        organization?.ID === account.OrganizationID ? organization : undefined,
      organizationLoadFailed: isOrganizationError
    })
  }, [session, account, organization, isOrganizationError, setAuthContext])

  // Tag Sentry events with the signed-in user so errors show who hit them, not
  // just an IP. Cleared on sign-out so a shared device does not misattribute.
  useEffect(() => {
    if (account) {
      Sentry.setUser({
        id: account.ID,
        email: account.Email,
        username: `${account.FirstName} ${account.LastName}`.trim()
      })
      Sentry.setTag('organization_id', account.OrganizationID)
    } else {
      Sentry.setUser(null)
    }
  }, [account])

  if (isSessionPending) {
    return (
      <div className="h-screen">
        <SuspenseFallback />
      </div>
    )
  }

  return <div className="h-screen">{children}</div>
}
